"use client"

import * as React from "react"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"

const SelectContext = React.createContext(null);

const useSelectContext = () => {
    const context = React.useContext(SelectContext);
    if (!context) {
        throw new Error("Select components must be used within a <Select> provider");
    }
    return context;
}

const Select = ({ children, value, onValueChange, defaultValue, ...props }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [displayValue, setDisplayValue] = React.useState(null);
    const triggerRef = React.useRef(null);
    const contentRef = React.useRef(null);

    const currentValue = value !== undefined ? value : internalValue;

    const handleValueChange = (newValue, newDisplayValue) => {
        if (value === undefined) {
            setInternalValue(newValue);
        }
        setDisplayValue(newDisplayValue);
        onValueChange?.(newValue);
        setIsOpen(false);
    };

    // Close on click outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isOpen &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target) &&
                contentRef.current &&
                !contentRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);
    
    // CORREZIONE: Questo useEffect ora ricalcola il displayValue ogni volta che il valore esterno (prop) cambia.
    React.useEffect(() => {
        let found = false;
        React.Children.forEach(children, child => {
            if (child.type.displayName === "SelectContent") {
                React.Children.forEach(child.props.children, item => {
                    if (item && item.props && item.props.value === currentValue) {
                        setDisplayValue(item.props.children);
                        found = true;
                    }
                });
            }
        });
        if (!found) {
            setDisplayValue(null);
        }
    }, [currentValue, children]);
	    const contextValue = {
        value: currentValue,
        onValueChange: handleValueChange,
        isOpen,
        setIsOpen,
        displayValue,
        triggerRef,
        contentRef,
    };

    return (
        <SelectContext.Provider value={contextValue}>
            <div className="relative" {...props}>
                {children}
            </div>
        </SelectContext.Provider>
    );
};
Select.displayName = "Select";


const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
    const { setIsOpen, triggerRef } = useSelectContext();
    const handleRef = React.useCallback(node => {
        triggerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    }, [ref, triggerRef]);

    return (
        <button
            ref={handleRef}
            type="button"
            onClick={() => setIsOpen(prev => !prev)}
            className={cn(
                "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                className
            )}
            {...props}
        >
            {children}
            <ChevronDown className="h-4 w-4 opacity-50 ml-2" />
        </button>
    );
});
SelectTrigger.displayName = "SelectTrigger";


const SelectValue = ({ placeholder }) => {
    const { displayValue } = useSelectContext();
    return <span className="truncate">{displayValue || placeholder}</span>;
};
SelectValue.displayName = "SelectValue";

const SelectContent = React.forwardRef(({ className, children, ...props }, ref) => {
    const { isOpen, contentRef } = useSelectContext();
    const assignRefs = React.useCallback((node) => {
        contentRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            ref.current = node;
        }
    }, [ref, contentRef]);
    if (!isOpen) return null;
    return (
        <div
            ref={assignRefs}
            className={cn(
                "absolute z-50 w-full mt-1 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80",
                className
            )}
            style={{ top: '100%' }}
            {...props}
        >
            <div className="p-1">{children}</div>
        </div>
    );
});
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(({ className, children, value, ...props }, ref) => {
    const { onValueChange } = useSelectContext();
    return (
        <div
            ref={ref}
            className={cn(
                "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 hover:bg-accent",
                className
            )}
            onClick={() => onValueChange(value, children)}
            {...props}
        >
            {children}
        </div>
    );
});
SelectItem.displayName = "SelectItem";

export { Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
